import {HighLowTransformer as Transformer} from './high-low-transformer'
import { interpolate } from 'd3-interpolate'
import * as geo from 'd3-geo'
import { get as getProjection, FEATURES} from '..'
import interpolatedProjection from './interpolated-projection'
import { scaleLinear as scale } from 'd3-scale'
import usaLowZoom from './usa-low-zoom'

export default class NorthAmericaUsa extends Transformer {

  static match (d0, d1) {
    if (!d0) {
      return false
    }

    let hasUsa = !![d0.node.key, d1.node.key].find(key => key == FEATURES.USA)
      , ids = [FEATURES.NORTH_AMERICA]

    return d0.node.key != d1.node.key
      && hasUsa // usa is coming or going
      && (
        // The other map is north america
        !!ids.find(id => id == d0.node.key) ||
        !!ids.find(id => id == d1.node.key)
      )
  }

  constructor (enter, exit, opts) {
    super(enter, exit, opts)

    this.opacityScale = scale().domain(this.zoomOut ? [0, .02] : [.98, 1]) // Last 98%
                               .range(this.zoomOut ? [1, 0] : [0, 1])
                               .clamp(true)
  }

  /*
   * Configures the low resolution map objects.
   */
  configureLowRes (low, highData, opts) {
    let lowData = low.datum()
      , lowGeo = this._buildFeatureCollection(lowData.zoomedTo, lowData.geojson)
      , fill = low.selectAll('path.feature') // Determine the current color of USA
                .filter(d => d.id == highData.id)
                .style('fill')

    // Set the fit size on the projection
    lowData.projection.fitSize([opts.width, opts.height], lowGeo)

    // Grab the high res feature within the low res map
    let lowResFeature = this.low.selectAll('path')
                                .filter(feature => feature.id == highData.id)
                                .style('opacity', 0) // Hide until the transition ends

    this.lowResFeature = lowResFeature

    let lowResGeo = lowResFeature.datum()
      , mainlandGeo = {
        type: 'Feature',
        id: FEATURES.USA,
        geometry: {
          type: 'MultiPolygon',
          coordinates: lowResGeo.geometry.coordinates.filter((d, i) => i == 0)
        }
      }
      , nonMainlandGeo = {
        type: 'Feature',
        id: FEATURES.USA,
        geometry: {
          type: 'MultiPolygon',
          coordinates: lowResGeo.geometry.coordinates.filter((d, i) => i > 0)
        }
      }

    let lowResEndJson = highData.geojson
      , lowResEndPath = geo.geoPath().projection(getProjection(highData.node, lowResEndJson, opts))

    lowResEndPath.projection()
                 .precision(Number.POSITIVE_INFINITY)

    // Re-insert as two features
    this.low.selectAll('path.usa')
            .data([mainlandGeo, nonMainlandGeo])
            .enter()
            .append('path')
            .attr('class', 'usa feature')
            .style('fill', fill)
            .each(function (d) {
              this._tween = interpolate(lowData.path(d), lowResEndPath(d))
            })

    // Determine the scales of the low feature
    let projection = getProjection(lowData.node, lowGeo, opts)
      , scale0 = projection.scale()
      , translate0 = projection.translate()

    // Now blow up the low res projection so it's focused on the high
    // res feature
    projection.fitSize([opts.width, opts.height], {
      type: 'FeatureCollection',
      features: this.lowZoomed(highData)
    })

    this.lowInterpolaters = {
      scale: interpolate(scale0, projection.scale()),
      translate: interpolate(translate0, projection.translate())
    }

    this.lowPath = lowData.path
  }

  /*
   * Configures the transformer's high resolution objects. The high
   * resolution feature's final projection is fit to its geojson. The feature
   * starts using a projection based on the the low resolution map zoomed
   */
  configureHighRes (high, lowData, opts) {
    let highData = high.datum()
      , lowGeo = this._buildFeatureCollection(lowData.zoomedTo, lowData.geojson)
      , lowProjection = getProjection(lowData.node, lowGeo, opts)
      , lowPath = geo.geoPath().projection(lowProjection)

    highData.projection.fitSize([opts.width, opts.height], highData.geojson)

    let features = high.selectAll('path.feature') // Store a copy before the clon
                       .attr('d', d => {
                         return highData.path(d)
                       })
    features.filter(d => d.id == FEATURES.ALASKA).raise()

    // Store an original copy of the full path, so we can use it at the end
    // of the transition
    features.clone(true)
            .attr('class', 'feature-final-projection feature')
            .style('opacity', 0)

    features.each(function (d) {
      // Attach an interpolater on each new path that uses
      // the previous map's projection as the start and the new high
      // path as the end

      let originals = d.geometry.coordinates
        , coords = [...d.geometry.coordinates] // Clone the coords
        , coord = coords[0]
        , coordThreshold = d.id == FEATURES.HAWAII ? 7 : 1 // Only worry about the top detailed coordinates

      if (d.id == FEATURES.MICHIGAN) {
        coordThreshold = 2
      }

      if (coords.length > coordThreshold) {
        // Sort the coords so the most detail is at the top of the list
        coords.sort((a, b) => {
          return b[0].length - a[0].length
        })
        coords.length = coordThreshold
      }
      d.geometry.coordinates = coords
      this._tween = interpolate(lowPath(d), highData.path(d))
      d.geometry.coordinates = originals
    })
  }

  end () {
    this.high
        .selectAll('path:not(.feature-final-projection)')
        .remove()
    this.high
        .selectAll('path.feature-final-projection')
        .classed('feature-final-projection', false)
        .style('opacity', 1)

    if (this.zoomOut) {
      this.low
          .selectAll('path.usa')
          .remove()
      this.lowResFeature.style('opacity', 1)
    }
  }

  /*
   * When we're zooming into the USA we want to take the old projection (i.e. the world or north america)
   * and blow it up to the new feature. Previously we took the high resolution data of the US and removed
   * Alaska/Hawaii so it blew up the continental US. That doesn't work if we're showing a map of the USA
   * without children. This approach builds 3 features (I think I used Washington, Maine, and Florida)
   * with coordinates of the external polygon ring.
   */
  lowZoomed () {
    return usaLowZoom
  }

  /*
   * High resolution is always the usa
   */
  resolutions (enter, exit) {
    let high = enter.datum().node.key == FEATURES.USA ? enter : exit
      , low = enter.datum().node.key != FEATURES.USA ? enter : exit // opposite

    return {high, low}
  }

  transform (t) {
    if (this.zoomOut) {
      // Reverse the transformation
      t = 1 - t
    }

    if (t > .98) {
      this.high
          .selectAll('path.feature-final-projection')
          .style('opacity', this.opacityScale(t))
    }

    this.high
        .selectAll('path.feature:not(.feature-final-projection')
        .attr('d', function () {
          return this._tween(t)
        })

    this.low
        .datum().projection
        .scale(this.lowInterpolaters.scale(t))
        .translate(this.lowInterpolaters.translate(t))

    this.low
        .selectAll('path:not(.usa)')
        .attr('d', d => this.lowPath(d))

    this.low.selectAll('path.usa')
      .attr('d', function () {
        return this._tween(t)
      })
  }

}
